import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Mail, Phone, Calendar, Crown, Heart, Eye, Settings,
  Home, Building2, Car, MapPin, DollarSign, Target, Briefcase,
  Edit2, Check, X, Shield, CreditCard, LogOut, ArrowLeft
} from 'lucide-react';
import Nav from '@/components/Nav';
import MyActivityLog from '@/components/MyActivityLog';
import LifetimeMiniCard from '@/components/LifetimeMiniCard';
import QuotationsInbox from '@/components/QuotationsInbox';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountPage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccountData = async () => {
    try {
      const response = await axios.get(`${API}/auth/account`);
      setAccountData(response.data);
      setEditForm({
        name: response.data.user.name || '',
        phone: response.data.user.phone || ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load account data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/account`, editForm);
      setAccountData(prev => ({
        ...prev,
        user: { ...prev.user, ...editForm }
      }));
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AE', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'elite': return 'text-purple-400';
      case 'premium': return 'text-gold';
      default: return 'text-text-secondary';
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'elite': return 'bg-purple-500/20 text-purple-400';
      case 'premium': return 'bg-gold/20 text-gold';
      default: return 'bg-white/10 text-text-secondary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading account...</div>
      </div>
    );
  }

  const { user, dream_profile, subscription, activity } = accountData || {};

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav showBack activePage="/account" />

      <div className="max-w-[1200px] mx-auto px-8 py-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-playfair text-4xl font-semibold mb-2">My Account</h1>
          <p className="text-text-secondary">Manage your profile and preferences</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-xl font-semibold">Profile</h2>
              {!editing ? (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={handleSaveProfile} disabled={saving} className="bg-gold text-black">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-gold/30 to-purple-500/30 rounded-full flex items-center justify-center">
                <User className="w-12 h-12 text-gold" />
              </div>
            </div>

            {editing ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-text-secondary">Name</Label>
                  <Input 
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-text-secondary">Phone</Label>
                  <Input 
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1"
                    placeholder="+971 50 123 4567"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4 text-center">
                <div>
                  <p className="text-2xl font-semibold">{user?.name || 'User'}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${getTierBadge(user?.subscription_tier)}`}>
                    {user?.subscription_tier?.toUpperCase() || 'FREE'} MEMBER
                  </span>
                </div>
                
                <div className="space-y-3 pt-4 text-left">
                  <div className="flex items-center gap-3 text-text-secondary">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{user?.email}</span>
                  </div>
                  {user?.phone && (
                    <div className="flex items-center gap-3 text-text-secondary">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{user?.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-text-secondary">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Joined {formatDate(user?.created_at)}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Activity & Subscription */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Activity Stats */}
            <div className="glass-card p-6">
              <h3 className="font-playfair text-lg font-semibold mb-4">Activity</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{activity?.wishlist_count || 0}</p>
                  <p className="text-text-secondary text-sm">Wishlist Items</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <Eye className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{activity?.viewings_count || 0}</p>
                  <p className="text-text-secondary text-sm">Viewing Requests</p>
                </div>
              </div>
            </div>

            {/* Subscription */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-playfair text-lg font-semibold">Subscription</h3>
                <Crown className={`w-5 h-5 ${getTierColor(user?.subscription_tier)}`} />
              </div>
              
              <div className={`inline-block px-4 py-2 rounded-lg text-lg font-semibold mb-4 ${getTierBadge(user?.subscription_tier)}`}>
                {user?.subscription_tier?.toUpperCase() || 'FREE'} Plan
              </div>
              
              {subscription?.features?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-text-secondary text-sm mb-2">Your benefits:</p>
                  {subscription.features.slice(0, 4).map((feature, idx) => (
                    <div key={`pref-${idx}`} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-gold" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {user?.subscription_tier !== 'elite' && (
                <Button 
                  onClick={() => navigate('/subscriptions')}
                  className="w-full mt-4 bg-gold text-black hover:bg-gold-light"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
              )}
            </div>
          </motion.div>

          {/* Dream Profile / Onboarding Data */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-playfair text-lg font-semibold">Dream Profile</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/onboarding')}
              >
                <Edit2 className="w-4 h-4 mr-1" /> Update
              </Button>
            </div>

            {dream_profile ? (
              <div className="space-y-6">
                {/* Personal Info */}
                {dream_profile.personal && (
                  <div>
                    <p className="text-text-secondary text-sm mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" /> Personal
                    </p>
                    <div className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                      {dream_profile.personal.occupation && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Occupation</span>
                          <span>{dream_profile.personal.occupation}</span>
                        </div>
                      )}
                      {dream_profile.personal.income_range && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Income</span>
                          <span>{dream_profile.personal.income_range}</span>
                        </div>
                      )}
                      {dream_profile.personal.family_status && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Family</span>
                          <span>{dream_profile.personal.family_status}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Home Preferences */}
                {dream_profile.home_preferences && (
                  <div>
                    <p className="text-text-secondary text-sm mb-2 flex items-center gap-2">
                      <Home className="w-4 h-4" /> Home Preferences
                    </p>
                    <div className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                      {dream_profile.home_preferences.preferred_locations?.length > 0 && (
                        <div>
                          <span className="text-text-secondary">Locations: </span>
                          <span className="text-gold">{dream_profile.home_preferences.preferred_locations.join(', ')}</span>
                        </div>
                      )}
                      {dream_profile.home_preferences.property_types?.length > 0 && (
                        <div>
                          <span className="text-text-secondary">Types: </span>
                          <span>{dream_profile.home_preferences.property_types.join(', ')}</span>
                        </div>
                      )}
                      {dream_profile.home_preferences.min_bedrooms && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Min Bedrooms</span>
                          <span>{dream_profile.home_preferences.min_bedrooms}+</span>
                        </div>
                      )}
                      {dream_profile.home_preferences.max_budget && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Budget</span>
                          <span className="text-gold">Up to {formatPrice(dream_profile.home_preferences.max_budget)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lifestyle Preferences */}
                {dream_profile.lifestyle_preferences && (
                  <div>
                    <p className="text-text-secondary text-sm mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> Lifestyle
                    </p>
                    <div className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                      {dream_profile.lifestyle_preferences.interests?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {dream_profile.lifestyle_preferences.interests.map((interest, idx) => (
                            <span key={`loc-${idx}`} className="px-2 py-0.5 bg-gold/20 text-gold rounded text-xs">
                              {interest}
                            </span>
                          ))}
                        </div>
                      )}
                      {dream_profile.lifestyle_preferences.travel_style && (
                        <div className="flex justify-between mt-2">
                          <span className="text-text-secondary">Travel Style</span>
                          <span>{dream_profile.lifestyle_preferences.travel_style}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Investment Goals */}
                {dream_profile.investment_goals && (
                  <div>
                    <p className="text-text-secondary text-sm mb-2 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> Investment Goals
                    </p>
                    <div className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                      {dream_profile.investment_goals.primary_goal && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Goal</span>
                          <span>{dream_profile.investment_goals.primary_goal}</span>
                        </div>
                      )}
                      {dream_profile.investment_goals.risk_tolerance && (
                        <div className="flex justify-between">
                          <span className="text-text-secondary">Risk</span>
                          <span>{dream_profile.investment_goals.risk_tolerance}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary mb-4">Complete your dream profile to get personalized recommendations</p>
                <Button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-gold text-black hover:bg-gold-light"
                >
                  Complete Profile
                </Button>
              </div>
            )}
          </motion.div>
        </div>

        {/* My Activity Log — Phase 2: customer-visible journey log */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32 }}
          className="mt-8 space-y-6"
        >
          <LifetimeMiniCard />
          <QuotationsInbox />
          <MyActivityLog />
        </motion.div>

        {/* Quick Actions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <Button 
            onClick={() => navigate('/wishlist')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-white/10 hover:bg-white/5"
          >
            <Heart className="w-5 h-5 text-red-400" />
            <span>My Wishlist</span>
          </Button>
          <Button 
            onClick={() => navigate('/properties')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-white/10 hover:bg-white/5"
          >
            <Building2 className="w-5 h-5 text-gold" />
            <span>Browse Properties</span>
          </Button>
          <Button 
            onClick={() => navigate('/investor/insights')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-white/10 hover:bg-white/5"
          >
            <CreditCard className="w-5 h-5 text-purple-400" />
            <span>Portfolio Insights</span>
          </Button>
          <Button 
            onClick={() => navigate('/settings')}
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-white/10 hover:bg-white/5"
          >
            <Settings className="w-5 h-5 text-blue-400" />
            <span>Settings</span>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountPage;
